import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit,
  ChangeDetectorRef,
  SimpleChanges, AfterViewInit, OnChanges,
} from "@angular/core";
import { DisplayService } from "../../services/display.service";
import { transform } from "ol/proj.js";
import { Coordinate } from "ol/coordinate";
import { createStringXY } from 'ol/coordinate.js';
import { AppText } from "src/app/interfaces/common";

/*
  Coordinate component that displays the coordinates of the last click on the map.
  Composed of 2 boxes that displays coordinates.
    Top box: Displays coordinates in GPS(EPSG:4326) coordinates. Three formats are available: DD, DDM or DMS
    Bottom box: Display coordinates in config defined coordinate system, if not defined then ISN2016 is used.
                Usually is one of the local Iceland coordinate system(ISN93, ISN2016).
*/

//Config object for coordinates component. Object defined in config: this.applicationSettings?.option?.map?.map_tools?.coordinates
interface CoordinateConfig {
  epsg: string; //Coordinate system to display the coords in the bottom box. Example values: "8088", "3057"
  name: string; //Name displayed in the bottom box. Example values: "ISN2016", "ISN93"
  gpsFormat: "DD" | "DDM" | "DMS"; //GPS format of the GPS coordinates in top box.
}
@Component({
  selector: "app-coordinates",
  templateUrl: "./coordinates.component.html",
  styleUrls: ["./coordinates.component.css"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CoordinatesComponent implements OnInit, AfterViewInit, OnChanges {
  //click event with coordinates from map.component
  @Input() clickEventOnMapCoordinates: any;
  //The initial center. It comes either from config or if not set, default value set in map.component
  @Input() center: number[] = [];
  @Input() selText: AppText = {};
  @Input() applicationSettings: any;

  //Initial coordinates before 1 click on map, is middle of iceland in ISN2016(EPSG:8088)
  displayedCoordinatesValue: number[] = [2700000, 300000];
  //Circa middle of iceland in GPS(LatLong)-coords
  latLongCoordinatesValue: string = "N 65° 0' 0'' W 19° 0' 0''";
  //The projection our current application(mapObj) is in. The coordinates in clickEventOnMapCoordinates are always in this projection.
  mapProjection: string = "EPSG:3857";

  coordsContainer!: HTMLElement;
  coordsBoxes!: HTMLElement;
  icon!: HTMLElement;

  shouldCoordsShow: boolean = false;

  state: "closed" | "open" = "closed";
  
  //Initialize with default values
  coordinateConfig: CoordinateConfig = {
    epsg: "EPSG:8088",
    name: "ISN2016",
    gpsFormat: "DD"
  }

  constructor(
    public displayService: DisplayService,
    private changeDetector: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.shouldCoordsShow = this.displayService.getShowCoordinates();

    this.mapProjection = `EPSG:${this.applicationSettings?.option?.map?.epsg ?? "3857"}`

    this.coordinateConfig = {
      epsg: `EPSG:${this.applicationSettings?.option?.map?.map_tools?.coordinates?.epsg ?? "8088"}`,
      name: this.applicationSettings?.option?.map?.map_tools?.coordinates?.name ?? "ISN2016",
      gpsFormat: this.applicationSettings?.option?.map?.map_tools?.coordinates?.gpsFormat ?? "DD"
    }

    //console.log(`mapProjection is: ${this.mapProjection} | displayed coordinates projection is: ${this.coordinateConfig.epsg} | Format of GPS coords is: ${this.coordinateConfig.gpsFormat}`)
    
    this.setCoordinates(this.center, this.mapProjection, this.coordinateConfig.epsg)
    this.setGPSCoordinates(this.center, this.mapProjection, this.coordinateConfig.gpsFormat)

    if (this.displayService.getCoordinatesOpen() == true) {
      this.state = "open";
    } else {
      this.state = "closed";
    }

    this.displayService.interfaceToggle.subscribe(() => {
      // Trigger change detection to update the view when interface is toggled
      if (this.displayService.mapviewInterface.isSimple ||
          this.applicationSettings?.option?.map?.map_tools?.showCoordinates === 0
      ) {
        this.shouldCoordsShow = false;
      } else {
        this.shouldCoordsShow = true;
        this.state = this.applicationSettings?.option?.map?.map_tools?.showCoordinates === 2 ? "open" : "closed"
      }

      this.changeDetector.detectChanges();
    });
    
  }

  ngAfterViewInit(): void {
    this.coordsContainer = document.getElementById("coordsContainer") as HTMLElement;
    this.coordsBoxes = document.getElementById("coordsBoxes") as HTMLElement;
    this.icon = document.getElementById("coordsIcon") as HTMLElement;
  }

  ngOnChanges(changes: SimpleChanges): void {
    
    if (changes?.clickEventOnMapCoordinates && !changes.clickEventOnMapCoordinates.firstChange) {
      this.setCoordinates(this.clickEventOnMapCoordinates.coordinate, this.mapProjection, this.coordinateConfig.epsg)
      this.setGPSCoordinates(this.clickEventOnMapCoordinates.coordinate, this.mapProjection, this.coordinateConfig.gpsFormat)
    }
  }
 
  /*
    When called sets/updates coordinates in the bottom box.
    coords: The input coordinates
    inEPSG: The projection the input coords are in. Example value "EPSG:3057"
    targetEPSG: The projection we want to use to display to the user.

    The desired coordinate system to display it in can be configured in config per application: "map"->"map_tools"->"coordinate".
    If not configured then it will use ISN2016(8088).
  */
  setCoordinates(coords: Coordinate, inEPSG: string, targetEPSG: string): void {
    if (!coords || !inEPSG || !targetEPSG) return
    
    const coordsInRequestedProj: Coordinate = transform(coords, inEPSG, targetEPSG);
    const coordsInRequestedProjRounded: Coordinate = [Math.round(coordsInRequestedProj[0]), Math.round(coordsInRequestedProj[1])];
    this.displayedCoordinatesValue = coordsInRequestedProjRounded
  }
  /*
    When called sets/updates coordinates in the top box.
    coords: The input coordinates
    inEPSG: The projection the input coords are in. Example value "EPSG:3057"
  */
  setGPSCoordinates(coords: Coordinate, inEPSG: string, gpsCoordinateFormat: "DD" | "DDM" | "DMS"): void {
    if (!coords || !inEPSG) return
    //transform here returns coordinate in GPS(LonlaL) in decimal degrees format
    const LonLatDD: Coordinate = transform(coords, inEPSG, "EPSG:4326");
    //latLon in decimal degrees, ready for display. createStringXY(6) returns a function that converts coords to string with 6 fractional digits precision.
    const latLonDDDisplay: string = createStringXY(6)(LonLatDD.slice(0).reverse())//.split(",").reduce((acc, currval, i) => acc+= i == 0 ? `${currval.trim()}° N` : ` ${currval.trim()}° W`, "")
    //const OLlatLonDMS: string = toStringHDMS(LonLatDD)
  
    switch (gpsCoordinateFormat) {
      case "DD":
        this.latLongCoordinatesValue = latLonDDDisplay
        break
      case "DMS":
        const latLonDMS: string = this.displayService.decimal2DMS(LonLatDD)
        this.latLongCoordinatesValue = latLonDMS
        break
      case "DDM":
        const latLonDDM: string = this.displayService.decimal2DDM(LonLatDD.slice(0).reverse())
        this.latLongCoordinatesValue = latLonDDM
        break
      default:
        this.latLongCoordinatesValue = latLonDDDisplay
    }
    
  }

  coordsToClipboard(coords: any): void {
    navigator.clipboard.writeText(coords).then(
      //success
      () => {
        this.displayService.toastrShowSuccess({
          message: coords,
          title: this.selText.coordinatesCopied
        });

      },
      //failure
      (error) => {
        console.warn("Could not copy content to clipBoard: " + error)
      }
    )
    
  }

  flipState(): void {
    if (this.state === "closed") {
      this.state = "open";
      this.openAnimation();
      return;
    }
    if (this.state === "open") {
      this.state = "closed";
      this.closeAnimation();
      return;
    }
  }
  
  closeAnimation(): void {
    const keyframeObjectIcon = [
      {
        transform: "rotate(0deg)",
      },
      {
        transform: "rotate(360deg)",
      },
    ];

    /*
    const keyframeObjectCoordsBoxes = [
      {clipPath: "polygon(0% 0%, 100% 0, 100% 100%, 0% 100%)",},
      {clipPath: "polygon(100% 0, 100% 0, 100% 100%, 100% 100%)"}
    ];
    */

    this.icon = document.getElementById("coordsIcon") as HTMLElement;
    this.coordsBoxes = document.getElementById("coordsBoxes") as HTMLElement;
    this.icon.animate(keyframeObjectIcon, 400);
  }

  openAnimation(): void {
    const keyframeObjectIcon = [
      {
        transform: "rotate(360deg)",
      },
      {
        transform: "rotate(0deg)",
      },
    ];

    const keyframeObjectCoordsBoxes = [
      {
        clipPath: "polygon(100% 0, 100% 0, 100% 100%, 100% 100%)",
      },
      {
        clipPath: "polygon(0% 0%, 100% 0, 100% 100%, 0% 100%)",
      },
    ];
    
    //this.icon = document.getElementById("coordsIcon") as HTMLElement;
    this.icon = document.getElementById("coordsIcon") as HTMLElement;
    this.coordsBoxes = document.getElementById("coordsBoxes") as HTMLElement;
    this.icon.animate(keyframeObjectIcon, 400);
    this.coordsBoxes.animate(keyframeObjectCoordsBoxes, 300);
  }
}
